// @ts-check
/**
 * Confirms a link-driven organisation switch for a super admin. Looks the organisation up itself, so
 * nothing reaches the shared context until the user has agreed and the organisation is known to exist.
 * Only mount this component when a super admin is expected to interact with it.
 * The lookup is refused for anyone else.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '@material-tailwind/react';

import { TestableButton, TestableChip, TestableText } from '@components/general/TestableComponents';
import LoadingIcon from '@components/navigation/LoadingIcon';
import useOrganisation from '@datahooks/admin/organisation/useOrganisation';

export const SWITCH_MESSAGES = {
  checking: 'Checking the organisation in the link…',
  notFound: 'The organisation in the link was not found.',
  failed: "We couldn't check the organisation in the link.",
  inactive: 'This organisation is inactive.',
};

/**
 * @param {Object} props
 * @param {number} props.orgId - The organisation the link points at.
 * @param {string} [props.currentOrganisationName] - Where the user is now, for the Stay button.
 * @param {(organisation: { id: number, title: string }) => void} props.onConfirm
 * @param {() => void} props.onCancel - Also Escape, the backdrop, and Close after a failed lookup.
 */
const SwitchOrganisationDialog = ({ orgId, currentOrganisationName = '', onConfirm, onCancel }) => {
  const { orgData, isLoading, isFetched, isWaiting, error } = useOrganisation(orgId);

  const settled = isFetched && !isLoading && !isWaiting;
  // A missing organisation comes back as an empty result, not an error, so the id is the test.
  const organisation = orgData?.id ? orgData : null;
  const current = currentOrganisationName || 'your current organisation';

  let body;
  let footer;

  if (!settled && !error) {
    body = (
      <div className="flex items-center gap-3" data-testid="switch-org-checking">
        <LoadingIcon size={60} />
        <TestableText variant="small" dataTestId="switch-org-checking-text">{SWITCH_MESSAGES.checking}</TestableText>
      </div>
    );
    footer = (
      <TestableButton dataTestId="switch-org-cancel" variant="text" color="pink" onClick={onCancel}>
        Cancel
      </TestableButton>
    );
  } else if (error || !organisation) {
    body = (
      <TestableText dataTestId="switch-org-problem">
        {error ? SWITCH_MESSAGES.failed : SWITCH_MESSAGES.notFound}
      </TestableText>
    );
    footer = (
      <TestableButton dataTestId="switch-org-close" color="blue" onClick={onCancel}>
        Close
      </TestableButton>
    );
  } else {
    const explanation = `This link is for the ${organisation.title} Executive Summary. Opening it switches your whole dashboard from ${current} to ${organisation.title}, and you will stay on ${organisation.title} until you change organisation again.`;

    body = (
      <div className="space-y-3">
        <TestableText dataTestId="switch-org-explanation">{explanation}</TestableText>
        {organisation.org_status === 'inactive' && (
          <div className="flex items-center gap-2">
            <TestableChip dataTestId="switch-org-inactive" color="pink" size="sm" value="inactive" />
            <TestableText variant="small" dataTestId="switch-org-inactive-text">{SWITCH_MESSAGES.inactive}</TestableText>
          </div>
        )}
      </div>
    );
    footer = (
      <>
        <TestableButton dataTestId="switch-org-stay" variant="outlined" color="pink" onClick={onCancel}>
          Stay on {current}
        </TestableButton>
        <TestableButton dataTestId="switch-org-switch" color="blue" onClick={() => onConfirm(organisation)}>
          Switch to {organisation.title}
        </TestableButton>
      </>
    );
  }

  return (
    <Dialog open handler={onCancel}>
      <DialogHeader>{organisation ? 'Switch organisation?' : 'Shared link'}</DialogHeader>
      <DialogBody tabIndex={0} className="text-base outline-none">{body}</DialogBody>
      <DialogFooter className="flex justify-end gap-3">{footer}</DialogFooter>
    </Dialog>
  );
};

SwitchOrganisationDialog.propTypes = {
  orgId: PropTypes.number.isRequired,
  currentOrganisationName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default SwitchOrganisationDialog;